import { cva } from "class-variance-authority";
import { cn } from "../../utils";
import { Text } from "../Typography";
import type { TailwindFontSize } from "../Typography/typography";
import type { SpinnerColor, SpinnerProps, SpinnerSize } from "./types";

export const spinnerVariants = cva(
  "flex animate-spin rounded-full border-solid border-s-transparent border-e-transparent [animation-duration:0.6s]",
  {
    variants: {
      color: {
        white: "border-t-white border-b-white",
        primary: "border-t-primary-500 border-b-primary-500",
        neutral: "border-t-neutral-500 border-b-neutral-500",
        destructive: "border-t-danger-600 border-b-danger-600",
      },
      size: {
        "2xs": "h-3 w-3 border",
        xs: "h-4 w-4 border-2",
        sm: "h-8 w-8 border-2",
        md: "h-10 w-10 border-2",
        lg: "h-12 w-12 border-2",
        xl: "h-14 w-14 border-2",
      },
    },
    defaultVariants: {
      color: "primary",
      size: "sm",
    },
  },
);

const textSizeMap: Record<SpinnerSize, TailwindFontSize> = {
  "2xs": "sm",
  xs: "sm",
  sm: "md",
  md: "md",
  lg: "lg",
  xl: "lg",
};

const gapMap: Record<SpinnerSize, string> = {
  "2xs": "gap-2",
  xs: "gap-2",
  sm: "gap-3",
  md: "gap-3",
  lg: "gap-4",
  xl: "gap-4",
};

const textColorMap: Record<SpinnerColor, string> = {
  white: "text-white",
  primary: "text-primary-500",
  neutral: "text-neutral-500",
  destructive: "text-danger-600",
};

export const Spinner = ({
  text,
  textPosition = "bottom",
  color = "primary",
  size = "sm",
  textProps,
  ariaLabel = "Loading",
  className,
  ...props
}: SpinnerProps) => {
  const textSize = textSizeMap[size];
  const textClass = textColorMap[color];
  const gap = gapMap[size];

  const spinnerElement = (
    <div
      className={spinnerVariants({ color, size })}
      role="status"
      aria-label={ariaLabel}
    >
      <span className="sr-only">{ariaLabel}</span>
    </div>
  );

  if (!text) {
    return (
      <div className={cn("inline-flex", className)} {...props}>
        {spinnerElement}
      </div>
    );
  }

  const containerClasses = cn(
    "inline-flex items-center",
    gap,
    {
      "flex-col": textPosition === "bottom",
      "flex-col-reverse": textPosition === "top",
      "flex-row": textPosition === "end",
      "flex-row-reverse": textPosition === "start",
    },
    className,
  );

  return (
    <div className={containerClasses} {...props}>
      {spinnerElement}
      {text && (
        <Text
          {...textProps}
          size={textProps?.size ?? textSize}
          className={cn(textClass, textProps?.className)}
        >
          {text}
        </Text>
      )}
    </div>
  );
};
